import { useCallback, useEffect, useState } from 'react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
  ZAxis,
  ReferenceLine,
} from 'recharts'
import { signalsAPI } from '../services/api'
import Spinner from './Spinner'
import { apiErrorMessage } from '../utils/apiError'
import { useChartTheme } from './dashboard/chartTheme'
import { useSurfaceTheme } from '../theme/surfaceTheme'
import type {
  CalibrationBand,
  Phase2ModelPerformance,
  Phase2Options,
  Phase2Retrain,
  WalkForwardFold,
} from '../types/phase2Model'

/**
 * Admin diagnostics: how the Phase 2 model's latest retrains did, read-only.
 *
 * Everything is the stored training run as /api/signals/model-performance
 * returns it; nothing is recomputed here and nothing changes the engine. A
 * figure a run did not record says "not enough data", never a stand-in number.
 */

// Two series at most per chart: categorical slots 1 and 2 of the validated
// reference palette, stepped per surface (checked against #ffffff and
// #141b22 with the dataviz validator).
const SERIES = {
  light: { model: '#2a78d6', other: '#eb6834' },
  dark: { model: '#3987e5', other: '#d95926' },
} as const

const NOT_ENOUGH = 'not enough data'

const pct = (v: number | null | undefined) => (v == null ? NOT_ENOUGH : `${(v * 100).toFixed(1)}%`)
const brier = (v: number | null | undefined) => (v == null ? NOT_ENOUGH : v.toFixed(3))
const signedR = (v: number | null | undefined) =>
  v == null ? NOT_ENOUGH : `${v >= 0 ? '+' : ''}${v.toFixed(2)}R`
const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : 'unknown time')

const OPTION_ROWS: { key: keyof Phase2Options; label: string; describe: (o: Phase2Options) => string }[] = [
  {
    key: 'fallback_mode',
    label: 'Win probability with no model',
    describe: (o) =>
      o.fallback_mode === 'empirical'
        ? 'Record win rate in the setup’s Phase 1 score band'
        : 'Phase 1 score × 0.9 (original)',
  },
  {
    key: 'gate_mode',
    label: 'Phase 2 gate',
    describe: (o) =>
      o.gate_mode === 'ev'
        ? `Expected R above ${o.min_ev_r >= 0 ? '+' : ''}${o.min_ev_r.toFixed(2)}R`
        : 'Score at or above 0.6 (original)',
  },
  {
    key: 'deploy_check',
    label: 'Deploy check',
    describe: (o) =>
      o.deploy_check === 'ranking'
        ? 'Kept beats dropped in 4 of 5 walk-forward folds, and beats the base rate'
        : 'Checks 5a, 5b, 5c on the newest held-out signals (original)',
  },
  {
    key: 'model_type',
    label: 'Model',
    describe: (o) => (o.model_type === 'logistic' ? 'Logistic regression on 8 inputs' : 'Gradient boosting (original)'),
  },
  {
    key: 'cluster_weighting',
    label: 'Setups within 8h counted as one bet',
    describe: (o) => (o.cluster_weighting ? 'On' : 'Off (original)'),
  },
  {
    key: 'extra_context',
    label: 'Market context inputs',
    describe: (o) => (o.extra_context ? 'On: BTC trend, return vs BTC, target reach' : 'Off (original)'),
  },
  {
    key: 'phase1_momentum_sign',
    label: 'Phase 1 momentum sign',
    describe: (o) =>
      o.phase1_momentum_sign === 'trade_direction' ? 'The trade’s direction' : 'EMA9 vs EMA21 (original)',
  },
]

const Section = ({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) => (
  <div className="border-t border-gray-200 p-4">
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    {sub && <p className="mt-0.5 text-xs text-gray-500">{sub}</p>}
    <div className="mt-3">{children}</div>
  </div>
)

const NotEnough = ({ why }: { why: string }) => (
  <p className="text-sm text-gray-500">
    Not enough data: {why}
  </p>
)

const CalibrationView = ({ data }: { data: Phase2ModelPerformance['calibration'] }) => {
  const theme = useChartTheme()
  const series = SERIES[useSurfaceTheme()]
  if (!data) return <NotEnough why="the latest retrain did not reach the held-out test, or predates this record." />
  const points = (bands: CalibrationBand[]) =>
    bands
      .filter((b) => b.n > 0 && b.mean_predicted != null && b.actual_win_rate != null)
      .map((b) => ({ x: b.mean_predicted as number, y: b.actual_win_rate as number, n: b.n, band: `${b.low.toFixed(1)}–${b.high.toFixed(1)}` }))
  const model = points(data.model)
  const fallback = points(data.fallback)
  return (
    <>
      <div className="h-64" data-testid="calibration-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 8, right: 16, bottom: 24, left: 0 }}>
            <CartesianGrid stroke={theme.grid} strokeDasharray="0" vertical={false} />
            <XAxis type="number" dataKey="x" domain={[0, 1]} tick={theme.axisTick} tickFormatter={(v) => `${Math.round(v * 100)}%`}
              label={{ value: 'Predicted win chance', position: 'insideBottom', offset: -12, fill: theme.axisTick.fill, fontSize: 11 }} />
            <YAxis type="number" dataKey="y" domain={[0, 1]} tick={theme.axisTick} tickFormatter={(v) => `${Math.round(v * 100)}%`} width={44} />
            <ZAxis type="number" dataKey="n" range={[64, 64]} />
            <ReferenceLine segment={[{ x: 0, y: 0 }, { x: 1, y: 1 }]} stroke={theme.cursor} strokeDasharray="4 4" />
            <ChartTooltip
              contentStyle={theme.tooltip}
              formatter={(value: number, name: string) => [name === 'n' ? value : `${(value * 100).toFixed(1)}%`, name === 'x' ? 'predicted' : name === 'y' ? 'actual' : 'signals']}
            />
            <Legend verticalAlign="top" height={24} />
            <Scatter name="Model" data={model} fill={series.model} />
            <Scatter name="Phase 1 × 0.9 rule" data={fallback} fill={series.other} />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="text-gray-500">
            <tr>
              <th className="px-2 py-1 text-left font-medium">Predicted band</th>
              <th className="px-2 py-1 text-right font-medium">Model: signals</th>
              <th className="px-2 py-1 text-right font-medium">Model: actual win rate</th>
              <th className="px-2 py-1 text-right font-medium">Rule: signals</th>
              <th className="px-2 py-1 text-right font-medium">Rule: actual win rate</th>
            </tr>
          </thead>
          <tbody className="text-gray-700">
            {data.model.map((b, i) => {
              const f = data.fallback[i]
              if (!b.n && !f?.n) return null
              return (
                <tr key={b.low}>
                  <td className="px-2 py-1">{`${Math.round(b.low * 100)}–${Math.round(b.high * 100)}%`}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{b.n}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{b.n ? pct(b.actual_win_rate) : '—'}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{f?.n ?? 0}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{f?.n ? pct(f.actual_win_rate) : '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <p className="mt-1 text-xs text-gray-500">On the {data.test_samples} newest held-out real signals. The dashed line is a perfect match.</p>
      </div>
    </>
  )
}

const FoldsView = ({ folds }: { folds: WalkForwardFold[] }) => {
  const theme = useChartTheme()
  const series = SERIES[useSurfaceTheme()]
  const rows = folds.filter((f) => f.model_brier != null && f.base_rate_brier != null)
  if (!rows.length)
    return <NotEnough why="no walk-forward fold of the latest retrain recorded both its Brier score and its base rate." />
  const data = rows.map((f) => ({ fold: `Fold ${f.fold}`, model: f.model_brier, base: f.base_rate_brier, n: f.real_test }))
  return (
    <>
      <div className="h-56" data-testid="folds-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={theme.grid} vertical={false} />
            <XAxis dataKey="fold" tick={theme.axisTick} />
            <YAxis tick={theme.axisTick} width={44} domain={[0, 'auto']} />
            <ChartTooltip contentStyle={theme.tooltip} formatter={(v: number, name: string) => [v.toFixed(3), name]} />
            <Legend verticalAlign="top" height={24} />
            <Bar dataKey="model" name="Model Brier (lower is better)" fill={series.model} radius={[4, 4, 0, 0]} maxBarSize={36} />
            <Line dataKey="base" name="Base-rate Brier" stroke={series.other} strokeWidth={2} dot={{ r: 4 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="text-gray-500">
            <tr>
              <th className="px-2 py-1 text-left font-medium">Fold</th>
              <th className="px-2 py-1 text-right font-medium">Test signals</th>
              <th className="px-2 py-1 text-right font-medium">Model Brier</th>
              <th className="px-2 py-1 text-right font-medium">Base-rate Brier</th>
              <th className="px-2 py-1 text-right font-medium">Kept / dropped</th>
              <th className="px-2 py-1 text-right font-medium">Mean gross R kept vs dropped</th>
            </tr>
          </thead>
          <tbody className="text-gray-700">
            {folds.map((f) => (
              <tr key={f.fold}>
                <td className="px-2 py-1">{f.fold}</td>
                {f.skipped ? (
                  <td colSpan={5} className="px-2 py-1 text-gray-500">{`Not enough data: ${f.skipped}`}</td>
                ) : (
                  <>
                    <td className="px-2 py-1 text-right tabular-nums">{f.real_test ?? NOT_ENOUGH}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{brier(f.model_brier)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{brier(f.base_rate_brier)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{f.kept != null ? `${f.kept} / ${f.dropped}` : NOT_ENOUGH}</td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {f.mean_gross_r_kept != null && f.mean_gross_r_dropped != null
                        ? `${signedR(f.mean_gross_r_kept)} vs ${signedR(f.mean_gross_r_dropped)}`
                        : NOT_ENOUGH}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

const RetrainsView = ({ retrains }: { retrains: Phase2Retrain[] }) => {
  if (!retrains.length) return <NotEnough why="no retrain has been recorded." />
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead className="text-gray-500">
          <tr>
            <th className="px-2 py-1 text-left font-medium">Retrain</th>
            <th className="px-2 py-1 text-left font-medium">Result</th>
            <th className="px-2 py-1 text-right font-medium">Held-out signals</th>
            <th className="px-2 py-1 text-right font-medium">Kept: count, mean gross R</th>
            <th className="px-2 py-1 text-right font-medium">Dropped: count, mean gross R</th>
          </tr>
        </thead>
        <tbody className="text-gray-700">
          {[...retrains].reverse().map((r, i) => (
            <tr key={`${r.trained_at}-${i}`}>
              <td className="px-2 py-1">{when(r.trained_at)}</td>
              <td className="px-2 py-1">{r.deployed ? 'Deployed' : 'Rejected'}</td>
              <td className="px-2 py-1 text-right tabular-nums">{r.test_samples ?? NOT_ENOUGH}</td>
              <td className="px-2 py-1 text-right tabular-nums">
                {r.kept != null ? `${r.kept}, ${signedR(r.mean_gross_r_kept)}` : NOT_ENOUGH}
              </td>
              <td className="px-2 py-1 text-right tabular-nums">
                {r.dropped != null ? `${r.dropped}, ${signedR(r.mean_gross_r_dropped)}` : NOT_ENOUGH}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const InputsView = ({ retrains }: { retrains: Phase2Retrain[] }) => {
  const recorded = retrains.filter((r) => r.top_inputs && Object.keys(r.top_inputs).length).slice(-5)
  if (!recorded.length) return <NotEnough why="no retrain has recorded its top inputs yet." />
  const latest = recorded[recorded.length - 1].top_inputs as Record<string, number>
  const names = Object.keys(latest)
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead className="text-gray-500">
          <tr>
            <th className="px-2 py-1 text-left font-medium">Input (latest retrain’s top)</th>
            {recorded.map((r, i) => (
              <th key={`${r.trained_at}-${i}`} className="px-2 py-1 text-right font-medium">
                {r.trained_at ? new Date(r.trained_at).toLocaleDateString() : `Retrain ${i + 1}`}
              </th>
            ))}
            <th className="px-2 py-1 text-right font-medium">Change, first to last</th>
          </tr>
        </thead>
        <tbody className="text-gray-700">
          {names.map((name) => {
            const shares = recorded.map((r) => (r.top_inputs as Record<string, number>)[name])
            const first = shares[0]
            const last = shares[shares.length - 1]
            return (
              <tr key={name}>
                <td className="px-2 py-1 font-mono">{name}</td>
                {shares.map((v, i) => (
                  <td key={i} className="px-2 py-1 text-right tabular-nums">
                    {v == null ? 'not in top 8' : `${(v * 100).toFixed(1)}%`}
                  </td>
                ))}
                <td className="px-2 py-1 text-right tabular-nums">
                  {recorded.length < 2 || first == null || last == null
                    ? NOT_ENOUGH
                    : `${last - first >= 0 ? '+' : ''}${((last - first) * 100).toFixed(1)} pts`}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <p className="mt-1 text-xs text-gray-500">Each input’s share of the model’s weight, over the last {recorded.length} retrain{recorded.length === 1 ? '' : 's'} that recorded it.</p>
    </div>
  )
}

const OptionsView = ({ active, trained }: { active?: Phase2Options; trained?: Phase2Options | null }) => {
  if (!active) return <NotEnough why="the server did not report its settings." />
  return (
    <table className="min-w-full text-xs">
      <thead className="text-gray-500">
        <tr>
          <th className="px-2 py-1 text-left font-medium">Setting</th>
          <th className="px-2 py-1 text-left font-medium">In force now</th>
          <th className="px-2 py-1 text-left font-medium">Latest retrain used</th>
        </tr>
      </thead>
      <tbody className="text-gray-700">
        {OPTION_ROWS.map((row) => (
          <tr key={row.key} data-testid={`option-${row.key}`}>
            <td className="px-2 py-1">{row.label}</td>
            <td className="px-2 py-1">{row.describe(active)}</td>
            <td className="px-2 py-1 text-gray-500">{trained ? row.describe(trained) : NOT_ENOUGH}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const Phase2ModelPanel = () => {
  const [data, setData] = useState<Phase2ModelPerformance | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await signalsAPI.modelPerformance())
    } catch (err) {
      setError(apiErrorMessage(err, 'The Phase 2 model record could not be loaded.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const status =
    data?.status === 'trained'
      ? 'A model is serving'
      : data?.status === 'rejected'
      ? 'The latest retrain was rejected; the Phase 1 rule serves'
      : 'No model has been trained; the Phase 1 rule serves'

  return (
    <section className="rounded-lg border border-gray-200 bg-white" aria-label="Phase 2 model">
      <div className="p-4">
        <h2 className="text-lg font-semibold text-gray-900">Phase 2 model</h2>
        <p className="mt-1 text-sm text-gray-500">
          How the latest retrains did on signals they never saw. Read-only: nothing here changes the engine.
        </p>
        {data && (
          <p className="mt-2 text-sm text-gray-700" data-testid="phase2-status">
            {status}
            {data.trained_at ? ` · last retrain ${when(data.trained_at)}` : ''}
            {data.training_samples != null ? ` · ${data.training_samples} training signals` : ''}
          </p>
        )}
      </div>
      {loading && !data && (
        <div className="flex items-center gap-2 px-4 pb-4 text-sm text-gray-500">
          <Spinner size="sm" /> Loading…
        </div>
      )}
      {error && (
        <p className="px-4 pb-4 text-sm text-danger-700" role="alert">
          {error}
        </p>
      )}
      {data && (
        <>
          <Section title="Predicted vs actual win rate" sub="Latest retrain, by predicted band, with the number of signals in each.">
            <CalibrationView data={data.calibration} />
          </Section>
          <Section
            title="Walk-forward Brier score per fold"
            sub="Each fold trains on the past and is scored on the next stretch. Below the base-rate line is better than guessing the average."
          >
            <FoldsView folds={data.walk_forward_folds ?? []} />
          </Section>
          <Section title="Kept vs dropped, per retrain" sub="On each retrain’s held-out signals: what the Phase 2 gate would keep and drop, by mean gross R.">
            <RetrainsView retrains={data.retrains ?? []} />
          </Section>
          <Section title="Top inputs across the last 5 retrains">
            <InputsView retrains={data.retrains ?? []} />
          </Section>
          <Section title="Which Phase 2 settings are active">
            <OptionsView active={data.active_options} trained={data.trained_options} />
          </Section>
        </>
      )}
    </section>
  )
}

export default Phase2ModelPanel
